// @ts-nocheck - formatter / Table
import React, { useState } from 'react';
import {
  loadMoveTransactions,
  exportMoveTransactionData,
} from '../../../actions/keyCodeActions';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../common/Table';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import FormattedDate from '../../common/FormattedDate';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import TransactionStatusBadge from './TransactionStatusBadge';
import GameLink from '../../common/links/GameLink';
import UserLink from '../../common/links/UserLink';
import { useTranslation } from 'react-i18next';

export interface MovedListProps {
  reload?: any;
  currentUser?: any;
}

function MovedList({ reload, currentUser }: MovedListProps) {
  const location = useLocation();
  const allColumns = [
    'creationDate',
    'numberOfKeys',
    'gameTitle',
    'sourceKeyWorkflowName',
    'destinationKeyWorkflowName',
    'user',
    'status',
    'description',
  ];
  const defaultVisibleColumns = allColumns;
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<any>();
  const params = new URLSearchParams(location.search);
  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'movedlist-settings',
      getTableSettingsDefaults({
        defaultSort: 'creationDate',
        direction: 'desc',
        defaultVisibleColumns,
      }),
      null,
      loadMoveTransactions,
      setTransactions,
      reload,
      t('Failed to load move transactions')
    );

  let columnsConfig = [
    {
      Key: 'creationDate',
      Label: t('Uploaded date'),
      width: '10%',
      Render: (item: any) => (
        <Link to={'/keys/moved/' + item.id}>
          <FormattedDate value={item.creationDate} variant="default" />
        </Link>
      ),
    },
    {
      Key: 'numberOfKeys',
      Label: t('Key count'),
      width: '10%',
      className: 'text-right num no-wrap',
      Render: (item: any) =>
        item.numberOfKeys >= 0
          ? CommaNumber_NoDecimal(item.numberOfKeys)
          : '--',
    },
    {
      Key: 'gameTitle',
      Label: t('Game'),
      width: '20%',
      Render: (item: any) => (
        <GameLink
          gameId={item.gameId}
          skuId={item.skuId}
          gameTitle={item.gameTitle}
          skuName={item.skuName}
          key={item.moveTransactionId}
          currentUser={currentUser}
        />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'sourceKeyWorkflowName',
      Label: t('Source'),
      width: '10%',
      Render: (item: any) => (
        <NavLink to={`/settings/workflow/${item.sourceKeyWorkflowId}`}>
          {item.sourceKeyWorkflowName}
        </NavLink>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'destinationKeyWorkflowName',
      Label: t('Destination'),
      width: '10%',
      Render: (item: any) => (
        <NavLink to={`/settings/workflow/${item.destinationKeyWorkflowId}`}>
          {item.destinationKeyWorkflowName}
        </NavLink>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'user',
      Label: t('Uploaded by'),
      width: '10%',
      Render: (item: any) => (
        <>
          <UserLink id={item.userId} name={item.user} />
          <br />
          {item.userCompany}
        </>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'status',
      Label: t('Status'),
      width: '5%',
      Render: (item: any) => (
        <TransactionStatusBadge
          success={item.success}
          partialSuccess={item.partialSuccess}
          message={item.message}
          status={item.status}
        />
      ),
      Filter: {
        Render: (col: any) => {
          const value = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          return (
            <select
              className={`form-control ${value ? 'filtered' : ''}`}
              value={value}
              onChange={(el) => filterChange(col.Key, (el.target as HTMLSelectElement).value)}
            >
              <option value="">{t('All')}</option>
              <option value="failed">{t('Failed')}</option>
              <option value="partial success">{t('Partial success')}</option>
              <option value="pending authorisation">{t('Pending authorisation')}</option>
              <option value="pending">{t('Pending')}</option>
              <option value="success">{t('Success')}</option>
            </select>
          );
        },
      },
    },
    {
      Key: 'description',
      Label: t('Description'),
      width: '25%',
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return transactions ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        allColumns={allColumns}
        rows={transactions.data.map((x: any) => ({ Key: x.id, ...x }))}
        totalRowCount={transactions.totalRowCount}
        filteredRowCount={transactions.filteredRowCount}
        scopes={transactions.scopes}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportMoveTransactionData}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default MovedList;
